;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import { useCallback, useEffect, useRef, useState } from 'react';

const NAVIGATION_KEYS = new Set([
    'Tab',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Enter',
    'Space',
    'Home',
    'End',
    'PageUp',
    'PageDown',
    'Escape',
]);

/**
 * Hook to detect if the user is currently using keyboard navigation
 * Optimized for production with dynamic event listeners and state change prevention
 * @returns {boolean} True if the user is navigating with keyboard, false otherwise
 */
export default function useDetectKeyboard() {
    const [isKeyboard, setIsKeyboard] = useState(false);
    const lastInteractionRef = useRef('mouse');
    const mouseMoveListenerActiveRef = useRef(false);

    // Memoize handlers to prevent recreation on every render
    const handleMouseMove = useCallback(() => {
        // Prevent unnecessary re-renders during rapid mouse/keyboard switching
        if (isKeyboard) {
            setIsKeyboard(false);
        }
        lastInteractionRef.current = 'mouse';

        // Remove the mouse listener since we're back to mouse mode
        if (mouseMoveListenerActiveRef.current) {
            window.removeEventListener('mousemove', handleMouseMove);
            mouseMoveListenerActiveRef.current = false;
        }
    }, [isKeyboard]);

    const handleKeyDown = useCallback(
        event => {
            if (NAVIGATION_KEYS.has(event.key)) {
                // Prevent unnecessary re-renders during rapid mouse/keyboard switching
                if (!isKeyboard) {
                    setIsKeyboard(true);
                }
                lastInteractionRef.current = 'keyboard';

                // Only add mouse listener when we're actually in keyboard mode
                if (!mouseMoveListenerActiveRef.current) {
                    window.addEventListener('mousemove', handleMouseMove, {
                        passive: true,
                    });
                    mouseMoveListenerActiveRef.current = true;
                }
            }
        },
        [handleMouseMove, isKeyboard]
    );

    const handleMouseClick = useCallback(() => {
        if (lastInteractionRef.current === 'keyboard') {
            // Prevent unnecessary re-renders during rapid mouse/keyboard switching
            if (isKeyboard) {
                setIsKeyboard(false);
            }
            lastInteractionRef.current = 'mouse';

            // Remove mouse move listener if it's active
            if (mouseMoveListenerActiveRef.current) {
                window.removeEventListener('mousemove', handleMouseMove);
                mouseMoveListenerActiveRef.current = false;
            }
        }
    }, [handleMouseMove, isKeyboard]);

    useEffect(() => {
        // Use passive listeners where possible for better performance
        const keydownOptions = { passive: true };
        const clickOptions = { passive: true };

        // Always listen for keyboard and click events
        window.addEventListener('keydown', handleKeyDown, keydownOptions);
        window.addEventListener('click', handleMouseClick, clickOptions);
        // Note: mousemove listener is added/removed dynamically

        return () => {
            window.removeEventListener(
                'keydown',
                handleKeyDown,
                keydownOptions
            );
            window.removeEventListener('click', handleMouseClick, clickOptions);

            // Clean up mousemove listener if it's still active
            if (mouseMoveListenerActiveRef.current) {
                window.removeEventListener('mousemove', handleMouseMove);
                mouseMoveListenerActiveRef.current = false;
            }
        };
    }, [handleKeyDown, handleMouseClick, handleMouseMove]);

    return isKeyboard;
}
